import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Board, Card, boardApi, cardApi } from '../services/api';
import { CardModal } from '../components/CardModal';
import { PageNavbar } from '../components/PageNavbar';
import { NavArrowLeft, BoxIso } from 'iconoir-react';

export const ArchivedCards: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const [board, setBoard] = useState<Board | null>(null);
  const [archivedCards, setArchivedCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  useEffect(() => {
    loadBoard();
  }, [boardId]);

  // Détecter le paramètre card dans l'URL pour ouvrir automatiquement la modale
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cardId = urlParams.get('card');
    if (cardId && archivedCards.length > 0) {
      const card = archivedCards.find(c => c.id === parseInt(cardId, 10));
      if (card) {
        setSelectedCard(card);
      }
    }
  }, [archivedCards]);

  const loadBoard = async () => {
    if (!boardId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await boardApi.getOne(parseInt(boardId));
      setBoard(response.data);
      
      // Extraire toutes les cartes archivées
      const archived: Card[] = [];
      response.data.columns.forEach(column => {
        column.cards.forEach(card => {
          if (card.archived) {
            archived.push({ ...card, column_title: column.title } as any);
          }
        });
      });
      setArchivedCards(archived);
    } catch (err: any) {
      setError('Erreur lors du chargement des cartes archivées');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardUpdate = (updatedCard: Card) => {
    setArchivedCards(prev => 
      prev.map(card => card.id === updatedCard.id ? updatedCard : card)
    );
    if (selectedCard?.id === updatedCard.id) {
      setSelectedCard(updatedCard);
    }
  };

  const handleCardDelete = (cardId: number) => {
    setArchivedCards(prev => prev.filter(card => card.id !== cardId));
    setSelectedCard(null);
  };

  const handleCardArchive = async (cardId: number, archived: boolean) => {
    try {
      // Trouver la carte et sa colonne
      const cardToUpdate = archivedCards.find(c => c.id === cardId);
      if (!cardToUpdate || !board) return;

      // Trouver la colonne de la carte
      const column = board.columns.find(col => 
        col.cards.some(c => c.id === cardId)
      );
      
      if (!column) return;

      // Mettre à jour le statut archived via l'API
      await cardApi.update(column.id, cardId, {
        archived: archived
      });

      if (!archived) {
        // Si la carte est désarchivée, la retirer de la liste
        setArchivedCards(prev => prev.filter(card => card.id !== cardId));
        setSelectedCard(null);
      }
      
      await loadBoard();
    } catch (err) {
      console.error('Erreur lors du désarchivage de la carte', err);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="archived-cards-page">
        <div className="loading-message">Chargement...</div>
      </div>
    );
  }

  if (error || !board) {
    return (
      <div className="archived-cards-page">
        <div className="error-message">{error || 'Tableau introuvable'}</div>
        <button className="btn btn-primary" onClick={handleBack}>
          Retour à l'administration
        </button>
      </div>
    );
  }

  return (
    <div className="archived-cards-page">
      <PageNavbar title={`Cartes archivées - ${board.title}`} />
      
      <div className="archived-cards-container">
        <div className="archived-cards-header">
          <button className="btn-back" onClick={handleBack}>
            <NavArrowLeft width={20} height={20} strokeWidth={2} />
            <span>Retour</span>
          </button>
        </div>

        <div className="archived-cards-content">
        {archivedCards.length === 0 ? (
          <div className="empty-message">
            <BoxIso width={48} height={48} strokeWidth={1.5} />
            <p>Aucune carte archivée</p>
          </div>
        ) : (
          <div className="archived-cards-grid">
            {archivedCards.map(card => (
              <div
                key={card.id}
                className="archived-card"
                onClick={() => setSelectedCard(card)}
              >
                {card.label_color && (
                  <div
                    className="archived-card-label"
                    style={{ backgroundColor: card.label_color }}
                  />
                )}
                <h3 className="archived-card-title">{card.title}</h3>
                {card.description && (
                  <p className="archived-card-description">
                    {card.description.substring(0, 100)}
                    {card.description.length > 100 ? '...' : ''}
                  </p>
                )}
                <div className="archived-card-meta">
                  <span className="archived-card-column">{(card as any).column_title}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {selectedCard && (
        <CardModal
          card={selectedCard}
          isOpen={true}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          onDelete={handleCardDelete}
          onArchive={handleCardArchive}
          userRole="admin"
        />
      )}
    </div>
  );
};
